import React, { Suspense, useCallback, useContext, useEffect, useState } from 'react';
import styles from './styles.module.pcss';
import { Calendar, Clock, FileText, MapPin, Users } from 'react-feather';
import WidgetMaskItem from './Scheduling/Components/WidgetMaskItem';
import { Popup } from 'antd-mobile';
import { WidgetUI } from '@/types';
import { useReactiveVar } from '@apollo/client';
import { currentUserVar } from '@/graphql/operations/user';
import { Loader } from '@/components';
import LayoutContext, { IFlexUserLayoutContext } from '@/context/FlexUserLayoutContext';
import { ResourceUIType } from '@/service/Api/types';
import useGetLayoutPermissions from '@/utils/hooks/useGetLayoutPermissions';

const ClockInClockOutWidget = React.lazy(() => import('./ClockInClockOutWidget'));
const Approvals = React.lazy(() => import('./Approval'));

const Widgets = () => {
  const currentUser = useReactiveVar(currentUserVar);
  const config = currentUser?.organization?.config;
  const layoutContext = useContext<IFlexUserLayoutContext>(LayoutContext);

  const { layoutPermissions } = useGetLayoutPermissions(
    [
      ResourceUIType.Availability,
      ResourceUIType.Schedules,
      ResourceUIType.Timekeeping,
      ResourceUIType.Compliance,
      ResourceUIType.Approvals,
    ],
    currentUser.permissions?.layoutAccess
  );

  const hasPreferenceAgentEnabled =
    !config?.disableAvailability &&
    layoutPermissions.get(ResourceUIType.Availability)?.includes('read') &&
    config?.enablePreferenceAgent;

  const toggleWidgetPopup = () => {
    layoutContext.toggleWidgetPopup?.();
  };

  const closeAll = () => {
    layoutContext.toggleWidgetPopup?.();
    layoutContext.toggleWidgetMask?.();
  };

  const setActiveWidget = useCallback(
    (name: WidgetUI) => {
      layoutContext.setCurrentActiveWidget?.(name);
      layoutContext.toggleWidgetPopup?.();
    },
    [layoutContext]
  );

  let widgetItems = new Map<
    WidgetUI,
    | { text: string; icon: React.ReactElement; disabled: boolean; path?: string; content?: React.ReactElement }
    | undefined
  >([
    [
      WidgetUI.Schedule,
      !config.enableScheduling || !layoutPermissions.get(ResourceUIType.Schedules)?.includes('read')
        ? undefined
        : {
            text: 'Schedule',
            icon: <Calendar size={30} />,
            disabled: false,
            path: '/my-schedule',
          },
    ],
    [
      WidgetUI.RoutePlanner,
      !config.enableScheduling ||
      !config.enableScheduleRoutePlanner ||
      !layoutPermissions.get(ResourceUIType.Schedules)?.includes('read')
        ? undefined
        : {
            text: 'Route Planner',
            icon: <MapPin size={30} />,
            disabled: false,
            path: '/my-schedule-route-planner',
          },
    ],
    [
      WidgetUI.ClockInClockOut,
      !layoutPermissions.get(ResourceUIType.Timekeeping)?.includes('read')
        ? undefined
        : {
            text: 'Time Tracker',
            icon: <Clock size={30} />,
            disabled: false,
            content: (
              <Suspense fallback={<Loader />}>
                <ClockInClockOutWidget onClose={closeAll} />
              </Suspense>
            ),
          },
    ],
    [
      WidgetUI.Availability,
      hasPreferenceAgentEnabled
        ? {
            text: 'Preferences',
            icon: <Users size={30} />,
            disabled: false,
            path: '/employee-preferences-agent',
          }
        : undefined,
    ],
    [
      WidgetUI.Compliance,
      !layoutPermissions.get(ResourceUIType.Compliance)?.includes('read')
        ? undefined
        : {
            text: 'Renew Credentials',
            icon: <FileText size={30} />,
            disabled: false,
            path: '/employee-compliance-agent',
          },
    ],
  ]);

  const widgetItemsForManager = new Map([
    [
      WidgetUI.Approval,
      !config.enableScheduling || !layoutPermissions.get(ResourceUIType.Schedules)?.includes('read')
        ? undefined
        : {
            text: 'Approvals',
            icon: <Users size={30} />,
            disabled: false,
            content: (
              <Suspense fallback={<Loader />}>
                <Approvals onClose={toggleWidgetPopup} />,
              </Suspense>
            ),
          },
    ],
  ]);

  if (currentUser.permissions?.isManager) {
    widgetItems = new Map([...widgetItems, ...widgetItemsForManager]);
  }

  const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight);

  const updateWindowHeight = () => {
    setWindowHeight(window.innerHeight);
  };

  useEffect(() => {
    window.addEventListener('resize', updateWindowHeight);
    return () => {
      window.removeEventListener('resize', updateWindowHeight);
    };
  }, []);

  return (
    <>
      <div className={styles.root}>
        {Array.from(widgetItems.entries()).map(([widgetName, menuItem], index) => {
          if (!menuItem) return undefined;

          return (
            <WidgetMaskItem
              key={index}
              name={widgetName}
              {...menuItem}
              setActiveWidget={setActiveWidget}
              currentActiveWidget={layoutContext.currentActiveWidget}
            />
          );
        })}
      </div>
      <Popup
        visible={layoutContext.showWidgetPopup}
        onClose={toggleWidgetPopup}
        bodyStyle={{
          height: layoutContext.isMobile ? `${windowHeight}px` : 'calc(100vh - 102px)',
          width: layoutContext.isMobile ? '100%' : '430px',
          left: layoutContext.isMobile ? '0' : 'calc((100vw - 430px) / 2)',
          boxShadow: '0px -7px 14px 0px rgba(0, 0, 0, 0.15)',
          borderTopLeftRadius: '30px',
          borderTopRightRadius: '30px',
        }}
        destroyOnClose
        maskStyle={{ opacity: 0 }}
      >
        {layoutContext.currentActiveWidget ? widgetItems.get(layoutContext.currentActiveWidget)?.content : null}
      </Popup>
    </>
  );
};

export default Widgets;
